import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { cn } from 'lib/utils';
import { questionNumber } from './data';
import { useQuestionnaireStore } from 'store/questionnaire';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox } from 'components/ui/checkbox';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { getQuestionnaireV3 } from 'services/profile/api';
import { useMemo, useState } from 'react';

interface QuestionnaireWrapperProps {
  children: React.ReactNode;
}

const noSidebar = [
  QUESTIONNAIRE_CODE.FACESCAN,
  QUESTIONNAIRE_CODE.SPECIFY_MEDICAL_CONDITION,
  QUESTIONNAIRE_CODE.SPECIFY_MEDICATION,
];

export default function QuestionnaireWrapper({
  children,
}: QuestionnaireWrapperProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const { t } = useTranslation();

  const questionData = useQuestionnaireStore((store) => store.questionData);
  const setQuestionData = useQuestionnaireStore(
    (store) => store.setQuestionData
  );

  const [isLoading, setIsLoading] = useState(false);

  const onBack = async () => {
    if (code) {
      return navigate(-1);
    }

    setIsLoading(true);
    const response: any = await getQuestionnaireV3({
      retrieve_type: 'previous',
      question_sequence: questionData?.question_sequence || 1,
    });
    if (!response?.data) {
      setQuestionData(undefined);
      return navigate(-1);
    }

    if (response?.data?.length && response?.data[0]) {
      const nextQuestion = response?.data[0];
      setQuestionData(nextQuestion);
      setIsLoading(false);
    }
  };

  const questionTitle: Record<QUESTIONNAIRE_CODE, string> = useMemo(
    () => ({
      [QUESTIONNAIRE_CODE.BODY_TYPE]: t('body_type_question'),
      [QUESTIONNAIRE_CODE.FITNESS_LEVEL]: t('fitness_level_question'),
      [QUESTIONNAIRE_CODE.SLEEP_LEVEL]: t('sleep_level_question'),
      [QUESTIONNAIRE_CODE.SLEEP_QUALITY]: t('sleep_quality_question'),
      [QUESTIONNAIRE_CODE.STRESS_LEVELS]: t('stress_levels_question'),
      [QUESTIONNAIRE_CODE.EATING_HABITS]: t('eating_habits_question'),
      [QUESTIONNAIRE_CODE.WATER_CONSUMPTION]: t('water_consumption_question'),
      [QUESTIONNAIRE_CODE.ALCOHOL_CONSUMPTION]: t(
        'alcohol_consumption_question'
      ),
      [QUESTIONNAIRE_CODE.SMOKING]: t('smoking_question'),
      [QUESTIONNAIRE_CODE.MEDICAL_CONDITIONS]: t('medical_conditions_question'),
      [QUESTIONNAIRE_CODE.FAMILY_MEDICAL_CONDITIONS]: t(
        'family_medical_conditions_question'
      ),
      [QUESTIONNAIRE_CODE.ONGOING_ALLERGIES]: t('ongoing_allergies_question'),
      [QUESTIONNAIRE_CODE.SURGERY]: t('surgery_question'),
      [QUESTIONNAIRE_CODE.TAKING_MEDICATIONS]: t('taking_medication_question'),
      [QUESTIONNAIRE_CODE.FACESCAN]: t('face_scan_question'),
      [QUESTIONNAIRE_CODE.SPECIFY_MEDICAL_CONDITION]: t(
        'specify_medical_condition_question'
      ),
      [QUESTIONNAIRE_CODE.SPECIFY_MEDICATION]: t('specify_medication_question'),
    }),
    []
  );

  const sideQuestions: Record<
    QUESTIONNAIRE_CODE,
    { label: string; sequence: number }
  > = useMemo(
    () => ({
      [QUESTIONNAIRE_CODE.BODY_TYPE]: {
        label: t('body_complexion_label'),
        sequence: 1,
      },
      [QUESTIONNAIRE_CODE.FITNESS_LEVEL]: {
        label: t('fitness_level_label'),
        sequence: 2,
      },
      [QUESTIONNAIRE_CODE.SLEEP_LEVEL]: {
        label: t('sleep_level_label'),
        sequence: 3,
      },
      [QUESTIONNAIRE_CODE.SLEEP_QUALITY]: {
        label: t('sleep_quality_label'),
        sequence: 4,
      },
      [QUESTIONNAIRE_CODE.STRESS_LEVELS]: {
        label: t('stress_levels_label'),
        sequence: 5,
      },
      [QUESTIONNAIRE_CODE.EATING_HABITS]: {
        label: t('eating_habits_label'),
        sequence: 6,
      },
      [QUESTIONNAIRE_CODE.WATER_CONSUMPTION]: {
        label: t('water_consumption_label'),
        sequence: 7,
      },
      [QUESTIONNAIRE_CODE.ALCOHOL_CONSUMPTION]: {
        label: t('alcohol_consumption_label'),
        sequence: 8,
      },
      [QUESTIONNAIRE_CODE.SMOKING]: { label: t('smoking_label'), sequence: 9 },
      [QUESTIONNAIRE_CODE.MEDICAL_CONDITIONS]: {
        label: t('medical_conditions_label'),
        sequence: 10,
      },
      [QUESTIONNAIRE_CODE.SPECIFY_MEDICAL_CONDITION]: {
        label: t('specify_medical_condition_label'),
        sequence: 10,
      },
      [QUESTIONNAIRE_CODE.FAMILY_MEDICAL_CONDITIONS]: {
        label: t('family_medical_conditions_label'),
        sequence: 11,
      },
      [QUESTIONNAIRE_CODE.ONGOING_ALLERGIES]: {
        label: t('ongoing_allergies_label'),
        sequence: 12,
      },
      [QUESTIONNAIRE_CODE.SURGERY]: { label: t('surgery_label'), sequence: 13 },
      [QUESTIONNAIRE_CODE.TAKING_MEDICATIONS]: {
        label: t('taking_medication_label'),
        sequence: 14,
      },
      [QUESTIONNAIRE_CODE.SPECIFY_MEDICATION]: {
        label: t('specify_medication_label'),
        sequence: 14,
      },
      [QUESTIONNAIRE_CODE.FACESCAN]: { label: '', sequence: 15 },
    }),
    []
  );

  const questionSequence = questionData?.code
    ? questionNumber[questionData?.code]
    : 0;

  return (
    <div className='p-4 space-y-4'>
      <Button
        variant='outline'
        className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
        onClick={onBack}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className='w-4 h-4 mb-px mr-1 animate-spin' />
        ) : (
          <ArrowLeft className='w-4 h-4 mb-px mr-1' />
        )}{' '}
        {t('go_back')}
      </Button>

      <div className='pt-4 pb-2 border rounded-md shadow-sm'>
        <div className='px-3 pb-2 space-y-2 border-b md:px-4'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center text-sm rounded-full size-5 bg-primary/10'>
              <p className='font-semibold text-primary'>{questionSequence}</p>
            </div>
            <h2 className='font-semibold'>
              {questionData?.code ? questionTitle[questionData?.code] : ''}
            </h2>
          </div>
          <div className='space-y-1'>
            <div className='grid gap-[2px] md:gap-1 grid-cols-14'>
              {Array.from({ length: 14 }).map((_, index) => (
                <div
                  key={index.toString()}
                  className={cn(
                    'bg-[#E6E6E6] w-full h-2 rounded-full',
                    questionSequence > index + 1 && 'bg-primary'
                  )}
                />
              ))}
            </div>
            <p className='text-sm md:text-base'>
              {questionSequence - 1} {t('questionnaire_completed')}
            </p>
          </div>
        </div>
        <div className='grid grid-cols-12'>
          <div className='hidden col-span-3 md:block'>
            <ScrollArea className='h-[calc(100vh-18rem)] border-r'>
              <div>
                {Object.entries(sideQuestions).map(([key, value]) => {
                  if (noSidebar.includes(key as QUESTIONNAIRE_CODE))
                    return null;

                  return (
                    <div
                      key={key}
                      className='flex items-center gap-2 p-4 border-b'
                    >
                      <Checkbox
                        id={key}
                        checked={value.sequence < questionSequence}
                        disabled
                      />
                      <h2
                        className={cn(
                          'text-sm font-semibold leading-tight',
                          value.sequence < questionSequence && 'text-primary'
                        )}
                      >
                        {value.label}
                      </h2>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <div className='col-span-12 md:col-span-9'>{children}</div>
        </div>
      </div>
    </div>
  );
}
